import axiosInstance from './AxiosInstance';

const BASE = '/op/tramites';
const BASE_PORTAL = '/op/portal';
const BASE_INSP = '/op/inspecciones';

// ==========================
// INTERFACES
// ==========================

export interface TipoTramiteOP {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  tipoDocumentoSalida?: string;   // LICENCIA | PERMISO | CONSTANCIA | DICTAMEN
  codigoDocumento?: string;       // código en nomenclatura oficial (ej. LC, PAL, CSyEE)
  vigenciaMeses?: number;         // vigencia del documento emitido en meses (0 = sin vencimiento)
  costoDescripcion?: string;
  activo: boolean;
  requisitos?: RequisitoTramiteOP[];
}

export interface RequisitoTramiteOP {
  id: number;
  nombreRequisito: string;
  descripcion?: string;
  obligatorio: boolean;
  orden: number;
}

export interface TipoDocumentoOP {
  id: number;
  codigo: string;
  descripcion: string;
}

export interface DocumentoOP {
  id: number;
  idTramite: number;
  idRequisito?: number;
  nombreArchivo: string;
  rutaArchivo: string;
  tipoArchivo: string;
  descripcion?: string;
  esSolicitud: boolean;
  tamanoBytes?: number;
  fechaSubida: string;
  usuarioSubida?: string;
  estatus: string; // PENDIENTE | COMPLETO | INCOMPLETO | OBSERVADO | RECHAZADO
  observacion?: string;
  activo: boolean;
  requisito?: { id: number; nombreRequisito: string; obligatorio: boolean };
}

export interface OpDocumentoOficial {
  id: number;
  idTramite: number;
  folioValidacion?: string;
  tipoDocumento?: string;
  descripcion?: string;
  fechaEmision: string;
  fechaVencimiento?: string;
  estatusVigencia: string; // VIGENTE | VENCIDO | CANCELADO | RENOVADO
  rutaPDF?: string;
  rutaDOCX?: string;
  plantillaCodigo?: string;
  plantillaArchivo?: string;
  plantillaOrigen?: string;
  observaciones?: string;
  generadoPor?: string;
  activo: boolean;
}

export interface TramiteOP {
  id: number;
  folio: string;
  folioValidacion?: string;
  anno?: number;
  idTipoTramite: number;
  tipoDocumento?: string;
  tipoPersona?: string;
  razonSocial?: string;
  representanteLegal?: string;
  nombreSolicitante: string;
  primerApellido?: string;
  segundoApellido?: string;
  rfc?: string;
  curp?: string;
  telefono: string;
  celular?: string;
  correo: string;
  calleSolicitante?: string;
  numeroExteriorSol?: string;
  numeroInteriorSol?: string;
  coloniaSolicitante?: string;
  municipioDomicilio?: string;
  estadoDomicilio?: string;
  codigoPostal?: string;
  nomenclatura?: string;
  direccion?: string;
  observaciones?: string;
  expedienteFisico?: string;
  estatus: string;
  origen: string;
  activo: boolean;
  motivoCancelacion?: string;
  fechaRegistro: string;
  fechaSolicitud: string;
  usuarioRegistro?: string;
  tipoTramite?: Pick<TipoTramiteOP, 'id' | 'nombre' | 'codigo' | 'requisitos'>;
  expediente?: { id: number; numeroExpediente: string } | null;
  documentos?: DocumentoOP[];
  inspecciones?: InspeccionOP[];
  pagos?: PagoOP[];
  _count?: { documentos: number; inspecciones: number; pagos: number };
}

export interface InspeccionOP {
  id: number;
  idTramite: number;
  folio: string;
  tipoInspeccion?: string;
  inspector?: string;
  fechaProgramada: string;
  fechaRealizada?: string;
  resultado?: string;
  descripcion?: string;
  observaciones?: string;
  recomendaciones?: string;
  rutaFoto1?: string;
  rutaFoto2?: string;
  rutaFoto3?: string;
  tramite?: { folio: string; nombreSolicitante: string };
}

export interface PagoOP {
  id: number;
  idTramite: number;
  concepto: string;
  monto: number;
  moneda: string;
  referencia?: string;
  folioRecibo?: string;
  metodoPago?: string;
  banco?: string;
  estatus: string;
  fechaVencimiento?: string;
  fechaPago?: string;
  observaciones?: string;
}

export interface FiltrosTramitesOP {
  buscar?: string;
  folio?: string;
  idTipoTramite?: number;
  tipoDocumento?: string;
  estatus?: string;
  origen?: string;
  fechaInicio?: string;
  fechaFin?: string;
  anno?: number;
  activo?: boolean;
}

export interface EstadisticasOP {
  anno: number;
  total: number;
  porEstatus: Array<{ estatus: string; _count: { estatus: number } }>;
  porTipoConNombre: Array<{ tipo: string; cantidad: number }>;
  porDocumento: Array<{ tipoDocumento: string; _count: { tipoDocumento: number } }>;
  porMes: Array<{ mes: number; total: number }>;
}

// ==========================
// CATALOGOS
// ==========================

export const obtenerCatalogosOP = async () => {
  const res = await axiosInstance.get<{ tiposTramite: TipoTramiteOP[]; tiposDocumento: TipoDocumentoOP[] }>(`${BASE}/catalogos`);
  return res.data;
};

// ==========================
// TRAMITES
// ==========================

export const obtenerTramitesOP = async (filtros?: FiltrosTramitesOP) => {
  const res = await axiosInstance.get<TramiteOP[]>(BASE, { params: filtros });
  return res.data;
};

export const obtenerTramiteOPPorId = async (id: number) => {
  const res = await axiosInstance.get<TramiteOP>(`${BASE}/${id}`);
  return res.data;
};

export const crearTramiteOP = async (data: Partial<TramiteOP> & { latitud?: number; longitud?: number; urlMaps?: string }) => {
  const res = await axiosInstance.post<TramiteOP>(BASE, data);
  return res.data;
};

export const actualizarTramiteOP = async (id: number, data: Partial<TramiteOP>) => {
  const res = await axiosInstance.put<TramiteOP>(`${BASE}/${id}`, data);
  return res.data;
};

export const actualizarEstatusOP = async (id: number, estatus: string, comentario?: string, usuario?: string) => {
  const res = await axiosInstance.put(`${BASE}/${id}/estatus`, { estatus, comentario, usuario });
  return res.data;
};

export const cancelarTramiteOP = async (id: number, motivo?: string) => {
  const res = await axiosInstance.post(`${BASE}/${id}/cancelar`, { motivo });
  return res.data;
};

export const obtenerEstadisticasOP = async (anno?: number) => {
  const res = await axiosInstance.get<EstadisticasOP>(`${BASE}/estadisticas`, { params: { anno } });
  return res.data;
};

// URL para descarga del PDF oficial con QR
export const getUrlDocumentoOficialOP = (id: number) =>
  `${axiosInstance.defaults.baseURL}${BASE}/${id}/documento-oficial`;

export const descargarDocumentoOficialOP = async (id: number) => {
  const res = await axiosInstance.get(`${BASE}/${id}/documento-oficial`, {
    responseType: 'blob',
  });
  const contentType = res.headers['content-type'] || 'application/octet-stream';
  const blob = new Blob([res.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const disposition = res.headers['content-disposition'] || '';
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] || `documento-oficial-${id}.${contentType.includes('pdf') ? 'pdf' : 'docx'}`;

  if (contentType.includes('pdf')) {
    window.open(url, '_blank');
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

// ==========================
// HISTORIAL
// ==========================

export const obtenerHistorialOP = async (id: number) => {
  const res = await axiosInstance.get<any[]>(`${BASE}/${id}/historial`);
  return res.data;
};

export const agregarComentarioOP = async (id: number, comentario: string, usuario?: string) => {
  const res = await axiosInstance.post(`${BASE}/${id}/comentarios`, { comentario, usuario });
  return res.data;
};

// ==========================
// EXPEDIENTE
// ==========================

export const obtenerExpedienteOP = async (id: number) => {
  const res = await axiosInstance.get(`${BASE}/${id}/expediente`);
  return res.data;
};

export const actualizarExpedienteOP = async (id: number, data: any) => {
  const res = await axiosInstance.put(`${BASE}/${id}/expediente`, data);
  return res.data;
};

// ==========================
// DOCUMENTOS
// ==========================

export const subirDocumentoOP = async (idTramite: number, archivo: File, meta: {
  descripcion?: string; esSolicitud?: boolean; usuario?: string;
}) => {
  const form = new FormData();
  form.append('archivo', archivo);
  if (meta.descripcion) form.append('descripcion', meta.descripcion);
  form.append('esSolicitud', String(meta.esSolicitud ?? false));
  if (meta.usuario) form.append('usuario', meta.usuario);

  const res = await axiosInstance.post(`${BASE}/${idTramite}/documentos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const eliminarDocumentoOP = async (idTramite: number, documentoId: number, usuario?: string) => {
  const res = await axiosInstance.delete(`${BASE}/${idTramite}/documentos/${documentoId}`, { data: { usuario } });
  return res.data;
};

export const actualizarEstatusDocumentoOP = async (
  idTramite: number, docId: number, estatus: string, observacion?: string, usuario?: string
) => {
  const res = await axiosInstance.patch(`${BASE}/${idTramite}/documentos/${docId}/estatus`, { estatus, observacion, usuario });
  return res.data;
};

export const inactivarDocumentoOP = async (idTramite: number, docId: number, usuario?: string, motivo?: string) => {
  const res = await axiosInstance.patch(`${BASE}/${idTramite}/documentos/${docId}/inactivar`, { usuario, motivo });
  return res.data;
};

export const getUrlDescargaDocumento = (idTramite: number, documentoId: number) =>
  `${axiosInstance.defaults.baseURL}${BASE}/${idTramite}/documentos/${documentoId}/descargar`;

export const descargarDocumentoOP = async (idTramite: number, documentoId: number, nombreArchivo = 'documento') => {
  const res = await axiosInstance.get(`${BASE}/${idTramite}/documentos/${documentoId}/descargar`, {
    responseType: 'blob',
  });

  const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombreArchivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

// ==========================
// VIGENCIAS
// ==========================

export const obtenerVigenciasProximasOP = async (dias = 30) => {
  const res = await axiosInstance.get(`${BASE}/vigencias/proximas`, { params: { dias } });
  return res.data as Array<OpDocumentoOficial & { tramite: any }>;
};

export const obtenerDocumentoOficialOP = async (idTramite: number) => {
  const res = await axiosInstance.get<OpDocumentoOficial>(`${BASE}/${idTramite}/documento-oficial/registro`);
  return res.data;
};

// ==========================
// INSPECCIONES
// ==========================

export const obtenerInspeccionesOP = async (idTramite?: number) => {
  const res = await axiosInstance.get<InspeccionOP[]>(BASE_INSP, { params: { idTramite } });
  return res.data;
};

export const crearInspeccionOP = async (data: Partial<InspeccionOP> & { usuarioRegistro?: string }) => {
  const res = await axiosInstance.post<InspeccionOP>(BASE_INSP, data);
  return res.data;
};

export const actualizarInspeccionOP = async (id: number, data: Partial<InspeccionOP>) => {
  const res = await axiosInstance.put<InspeccionOP>(`${BASE_INSP}/${id}`, data);
  return res.data;
};

export const subirFotosInspeccionOP = async (id: number, fotos: File[]) => {
  const form = new FormData();
  fotos.slice(0, 3).forEach((foto) => form.append('fotos', foto));
  const res = await axiosInstance.post<InspeccionOP>(`${BASE_INSP}/${id}/fotos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

// ==========================
// PAGOS
// ==========================

export const obtenerPagosOP = async (idTramite: number) => {
  const res = await axiosInstance.get<PagoOP[]>(`${BASE}/${idTramite}/pagos`);
  return res.data;
};

export const registrarPagoOP = async (idTramite: number, data: Partial<PagoOP> & { usuarioRegistro?: string }) => {
  const res = await axiosInstance.post<PagoOP>(`${BASE}/${idTramite}/pagos`, data);
  return res.data;
};

// ==========================
// PORTAL CIUDADANO (sin auth)
// ==========================

export const consultarTramitePorFolio = async (folio: string) => {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
  const res = await fetch(`${base}/api/op/portal/consulta?folio=${encodeURIComponent(folio)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al consultar');
  }
  return res.json();
};

export const validarDocumentoPorFolioValidacion = async (folioValidacion: string) => {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
  const res = await fetch(`${base}/api/op/portal/validar?v=${encodeURIComponent(folioValidacion)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al validar');
  }
  return res.json();
};

export const obtenerTiposPublicoOP = async () => {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
  const res = await fetch(`${base}/api/op/portal/tipos`);
  if (!res.ok) throw new Error('Error al obtener tipos');
  return res.json() as Promise<TipoTramiteOP[]>;
};

export const obtenerRequisitosTipoOP = async (idTipo: number) => {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
  const res = await fetch(`${base}/api/op/portal/requisitos/${idTipo}`);
  if (!res.ok) throw new Error('Error al obtener requisitos');
  return res.json();
};

export const preRegistroOP = async (data: Record<string, any>) => {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
  const res = await fetch(`${base}/api/op/portal/pre-registro`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al realizar pre-registro');
  }
  return res.json();
};

export const tramiteDigitalOP = async (data: Record<string, any>) => {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
  const res = await fetch(`${base}/api/op/portal/tramite-digital`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al registrar trámite');
  }
  return res.json();
};

export const subirDocumentoDigitalOP = async (idTramite: number, archivo: File, descripcion?: string) => {
  const base = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';
  const form = new FormData();
  form.append('archivo', archivo);
  if (descripcion) form.append('descripcion', descripcion);
  const res = await fetch(`${base}/api/op/portal/tramite-digital/${idTramite}/documentos`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al subir documento');
  }
  return res.json();
};
